if (!customElements.get('webinar-access')) {
	class WebinarAccess extends HTMLElement {
	  constructor() {
		super();
		this.personalCabinet = null;
		this.pageContent = '';
		this.restrictedContent = '';
		this.notPurchasedContent = '';
		this.expiredContent = '';
		this.metaobjectProducts = [];
		this.currentPageUrl = '';
		this.handleCabinetUpdate = this.handleCabinetUpdate.bind(this);
	  }
  
	  connectedCallback() {
		this.personalCabinet = window['theme']?.personalCabinet ?? null;
		
		// Parse attributes
		this.pageContent = JSON.parse(unescape(this.getAttribute('data-page-content') || '""'));
		this.restrictedContent = JSON.parse(unescape(this.getAttribute('data-restricted-content') || '""'));
		this.notPurchasedContent = JSON.parse(unescape(this.getAttribute('data-not-purchased-content') || '""'));
		this.expiredContent = JSON.parse(unescape(this.getAttribute('data-expired-content') || '""'));
		this.metaobjectProducts = JSON.parse(unescape(this.getAttribute('data-metaobject-products') || '[]'));
		this.currentPageUrl = this.getAttribute('data-current-page-url') || '';

		this.removeAttribute('data-page-content');
		this.removeAttribute('data-restricted-content');
		this.removeAttribute('data-not-purchased-content');
		this.removeAttribute('data-expired-content');

		document.addEventListener('personal-cabinet:retrieved', this.handleCabinetUpdate);
		
		this.init();
	  }
  
	  disconnectedCallback() {
		document.removeEventListener('personal-cabinet:retrieved', this.handleCabinetUpdate);
	  }
  
	  handleCabinetUpdate() {
		this.personalCabinet = window['theme']?.personalCabinet ?? null;
		this.init();
	  }
  
	  showPageContent() {
		if (this.pageContent) {
		  this.innerHTML = this.pageContent;
		}
	  }

	  showRestrictedContent() {
		if (this.restrictedContent) {
		  this.innerHTML = this.restrictedContent;
		}
	  }

	  showNotPurchasedContent() {
		if (this.notPurchasedContent) {
		  this.innerHTML = this.notPurchasedContent;
		}
	  }

	  showExpiredContent() {
		if (this.expiredContent) {
		  this.innerHTML = this.expiredContent;
		}
	  }
  
	  // --- Helpers ---
	  normalizeUrl(u) {
		try {
		  const url = new URL(u, window.location.origin);
		  const noTrailingSlash = url.pathname.replace(/\/$/, '');
		  return `${url.origin}${noTrailingSlash}`;
		} catch (e) {
		  return String(u || '')
			.replace(/[?#].*$/, '')
			.replace(/\/$/, '');
		}
	  }
  
	  getCabinetArray() {
		if (!this.personalCabinet) return [];
		return Array.isArray(this.personalCabinet) ? this.personalCabinet : [this.personalCabinet];
	  }
  
	  // --- Main ---
	  init() {
		try {
		  const pageUrl = this.normalizeUrl(this.currentPageUrl);

		  // Collect ALL metaobjects whose link matches the current page
		  const matchingMetaobjects = (this.metaobjectProducts || []).filter((obj) => {
			const objUrl = this.normalizeUrl(obj?.link?.url);
			const matches = objUrl && pageUrl && objUrl === pageUrl;
			return matches;
		  });

		  // Check if customer is logged in first
		  const customerStatus = window['theme']?.customer?.customerStatus;
		  const isLoggedIn = customerStatus && customerStatus !== 'not_logged_in_or_no_orders';

		  if (!matchingMetaobjects.length) {
			this.showRestrictedContent();
			return;
		  }

		  // If not logged in, show restricted content
		  if (!isLoggedIn) {
			this.showRestrictedContent();
			return;
		  }

		  // Check customer tags first (priority access) - only for logged in users
		  const customerTags = window['theme']?.customer?.tags || [];
		  
		  // Check if any matching metaobject grants access via customer tags
		  let hasTagAccess = false;
		  for (const meta of matchingMetaobjects) {
			if (Array.isArray(meta.customer_tags) && meta.customer_tags.length > 0) {
				const hasMatchingTag = meta.customer_tags.some(tag => {
					const matches = customerTags.includes(tag);
					return matches;
				});
				
				if (hasMatchingTag) {
					hasTagAccess = true;
					break;
				}
			}
		  }
		  
		  if (hasTagAccess) {
			this.showPageContent();
			return;
		  }

		  if (!this.personalCabinet) {
			this.showNotPurchasedContent();
			return;
		  }

		  const seen = new Set();
		  const uniqueMatches = [];
		  for (const m of matchingMetaobjects) {
			const key = `${this.normalizeUrl(m?.link?.url)}::${String(m?.product?.id ?? 'no-id')}`;
			if (!seen.has(key)) {
			  seen.add(key);
			  uniqueMatches.push(m);
			}
		  }

		  const cabinet = this.getCabinetArray();
		  const now = Math.floor(Date.now() / 1000);

		  let hasAccess = false;
		  let hasExpired = false;

		  for (const meta of uniqueMatches) {
			// Check customer tags first (priority access)
			if (Array.isArray(meta.customer_tags) && meta.customer_tags.length > 0) {
				const customerTags = window['theme']?.customer?.tags || [];
				const hasMatchingTag = meta.customer_tags.some(tag => {
					const matches = customerTags.includes(tag);
					return matches;
				});
				
				if (hasMatchingTag) {
					hasAccess = true;
					continue;
				}
			}

			// If no product requirement, access is granted
			if (!meta.product || !meta.product.id) {
				hasAccess = true;
				continue;
			}

			// Match by product ID OR overlapping product_tags (helps when ids differ)
			const metaTags = Array.isArray(meta.product_tags) ? meta.product_tags : [];
			
			const found = cabinet.find((p) => {
			  const idMatches = String(p?.id) === String(meta.product.id);
			  const tagMatches =
				Array.isArray(p?.product_tags) &&
				metaTags.length > 0 &&
				metaTags.some((t) => p.product_tags.includes(t));
			  return idMatches || tagMatches;
			});

			if (!found) {
			  continue;
			}

			const hasDuration = Array.isArray(meta.duration) && meta.duration[0]?.days;
			if (!hasDuration) {
			  hasAccess = true;
			  continue;
			}

			const purchaseTimestamp = found?.bought_at ? Date.parse(found.bought_at) / 1000 : 0;
			const durationDays = Number(meta.duration[0].days);
			const accessExpiresAt = purchaseTimestamp + durationDays * 24 * 60 * 60;

			if (now >= accessExpiresAt) {
			  hasExpired = true;
			} else {
			  hasAccess = true;
			}
		  }

		  // Decision:
		  // - If ANY match grants access -> show content
		  // - Else if at least one relevant purchase is expired -> show expired
		  // - Else -> not purchased
		  if (hasAccess) {
			this.showPageContent();
		  } else if (hasExpired) {
			this.showExpiredContent();
		  } else {
			this.showNotPurchasedContent();
		  }
		} catch (error) {
		  this.showRestrictedContent();
		}
	  }
	}
  
	customElements.define('webinar-access', WebinarAccess);
  }  